import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeProvider } from "@material-ui/core";
import ScopedCssBaseline from "@material-ui/core/ScopedCssBaseline";
import { theme } from "./theme";
import Layout from "./Layout";
import { BrowserRouter } from "react-router-dom";
import Validator from "validator";
import ReactDOM from "react-dom";
import CancelReservaton from './components/CancelReservation';
import CallbackLoader from './components/CallbackLoader';
import { LocalizationProvider } from './components/LanguageContext/LocalizationContext';
import { LocalStorageKeys } from './settings';
import ServicesJSON from "./services.json";
import { QueryParams, QueryKeys, AuthCallbackState } from './types';
import useQuery from './hooks/useQuery';
import strongAuthService from './services/strongAuthService';
import { decodeObject } from './util/functions';
import memoryStorage from './util/memoryStorage';
import {
    noFilteringSearchParamsFound, removeQueryParameters, getSearchParam,
    getCampaignPath
} from './util/url';

if (process.env.NODE_ENV !== "production") {
    import('react-axe').then(Axe => {
        Axe.default(React, ReactDOM, 1000);
    });
}

const getCampaignUuid = () => {
    let uuid = memoryStorage.getItem('campaignUuid');

    if (!uuid) {
        const callbackState: string|null = localStorage.getItem('state');
        const paramState:    string|null = getSearchParam('state');
        if (callbackState && callbackState === paramState) {
            const stateObject: AuthCallbackState = decodeObject(callbackState);
            uuid = stateObject.campaignUuid;
        }
    }

    if (uuid) {
        // If the uuid is a subService we find its parent and use its uuid
        const parentService = ServicesJSON.find(pService =>
            pService.subServices.find(sService => sService.id === uuid));
        if (parentService) uuid = parentService.id;
    }

    if (!uuid) {
        const selectedLanguage = localStorage.getItem(LocalStorageKeys.languageSelection) || "finnish";

        // Match service by URL path segment, anchored to avoid substring collisions (e.g. "psykologipalvelut" matching "neuropsykologipalvelut")
        uuid = ServicesJSON.find(service => {
            const path = service.path as { [key: string]: string };

            return path && new RegExp(`(^|/)${path[selectedLanguage]}(/|$|\\?)`).test(window.location.pathname);
        })?.id || null;
    }

    if (!uuid) {
        const baseParam: string|null = getSearchParam('base');
        if (baseParam) uuid = baseParam;
    }

    if (
        !uuid &&
        (window.location.pathname === "/" &&
        noFilteringSearchParamsFound())
    ) {
        uuid = ServicesJSON[0].id;
    }

    memoryStorage.setItem('campaignUuid', uuid);

    return uuid;
};

export const QueryComponent: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const query = useQuery();
    const [authenticated, setAuthenticated] = useState<boolean>(false);

    const confirmAuthentication = async (
        paramRequestId: string,
        codeVerifier: string,
        state: AuthCallbackState
    ) => {
        const authSuccess = await strongAuthService.exchangeToken(paramRequestId, codeVerifier);

        if (authSuccess && !authenticated) {
            setAuthenticated(true);
            memoryStorage.setItem('reservationUuid', state.reservationUuid);
            memoryStorage.setItem('bookForOther', state.bookForOther);
            memoryStorage.setItem('campaignUuid', state.campaignUuid);

        } else if (!authSuccess && authenticated) {
            setAuthenticated(false);
        }

        removeQueryParameters(history, location, [ 'state', 'request_id' ]);
    };

    const setAuthenticationErrorState = (error: string, state: string) => {
        const stateObject: AuthCallbackState = decodeObject(state);
        if (stateObject) {
            memoryStorage.setItem('reservationUuid', stateObject.reservationUuid);
            memoryStorage.setItem('bookForOther', stateObject.bookForOther);
            memoryStorage.setItem('campaignUuid', stateObject.campaignUuid);
            memoryStorage.setItem('error', error);
        }
    };

    // Handle strong authentication callback.
    useEffect(() => {
        const paramState: string|null = query.get('state');
        const paramRequestId: string|null = query.get('request_id');
        const paramError: string|null = query.get('error');
        const storageState: string|null = localStorage.getItem('state');
        const codeVerifier = localStorage.getItem('codeVerifier');

        localStorage.removeItem('state');
        localStorage.removeItem('codeVerifier');

        if (
            codeVerifier &&
            paramRequestId &&
            storageState &&
            paramState === storageState
        ) {
            const stateObject: AuthCallbackState = decodeObject(storageState);

            confirmAuthentication(paramRequestId, codeVerifier, stateObject);
        } else {
            if (authenticated) setAuthenticated(false);

            if (paramState && paramError) {
                setAuthenticationErrorState(paramError, paramState);
            }
        }
    }, []);

    const cancelReservation = query.get("cancelReservation");

    const getQueryParams = (query: URLSearchParams): Partial<QueryParams> =>
        Object.keys(QueryKeys).reduce((acc, cur) => {
            const filters = (key: string) => {
                const filterParams = query.get(key);
                return filterParams ? filterParams
                    .split(";")
                    .filter(str => Validator.isUUID(str)) :
                    null;
            };
            const value = cur === "filters" || cur === "base" ? filters(cur) : query.get(cur);
            return value ? { ...acc, [cur]: value } : acc;
        }, {});

    const usedQueryParams = getQueryParams(query);

    return (
        cancelReservation ?
            <CancelReservaton uuid={cancelReservation} /> :
            <Layout
                campaignUuid={getCampaignUuid()}
                queryParams={usedQueryParams}
                authenticated={authenticated}
            />
    );
};

const App: React.FC = () => {
    if (window.location.pathname === '/callback') {
        const currentUrl = new URL(window.location.href);

        // Redirects user to the correct booking page or back to the landing page.
        window.location.href = `${currentUrl.protocol}//${currentUrl.host}${getCampaignPath()}`;

        return <CallbackLoader />;
    }

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme} >
                <ScopedCssBaseline />
                <LocalizationProvider>
                    <QueryComponent />
                </LocalizationProvider>
            </ThemeProvider >
        </BrowserRouter>
    );
};

export default App;
